import { Link, useLocation, useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "../auth/AuthContext";
import { FundraiserBanner } from "./FundraiserBanner";
import { BrandLogo } from "./BrandLogo";
import { UserAvatar } from "./UserAvatar";

// One list drives both navs. They used to be separate arrays, which is how the
// mobile bar ended up missing About, Guest book and Photos entirely.
// `short` is the bottom-bar label — the bar fits ~278px of text at 375px, so
// "Make a Pledge" is abbreviated there and spelled out in the header.
const NAV_ITEMS = [
  { path: "/", label: "Home", short: "Home", icon: HomeIcon },
  { path: "/about", label: "About", short: "About", icon: AboutIcon },
  { path: "/guestbook", label: "Guest book", short: "Guest book", icon: GuestbookIcon },
  { path: "/photos", label: "Photos", short: "Photos", icon: PhotosIcon },
  { path: "/pledge", label: "Make a Pledge", short: "Pledge", icon: PledgeIcon },
  { path: "/my-pledge", label: "My Pledge", short: "My Pledge", icon: MyPledgeIcon },
];

export function Chrome({ children }: { children: ReactNode }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "var(--bg)" }}>
      <DesktopHeader />
      <FundraiserBanner />
      <main style={{ flex: 1 }}>{children}</main>
      <Footer />
      <MobileNav />
    </div>
  );
}

function DesktopHeader() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        background: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(14px)",
        borderBottom: "1px solid var(--line)",
        padding: "12px 24px",
        display: "flex",
        alignItems: "center",
        gap: 28,
      }}
    >
      <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
        <BrandLogo size={22} />
      </Link>
      {/* No inline `display` here — .desktop-only owns it. An inline value
          outranks the stylesheet and the nav never hides on mobile. */}
      <nav style={{ gap: 22, alignItems: "center", flex: 1 }} className="desktop-only">
        {NAV_ITEMS.map((n) => (
          <Link
            key={n.path}
            to={n.path}
            style={{
              textDecoration: "none",
              color: loc.pathname === n.path ? "var(--accent)" : "var(--ink-soft)",
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            {n.label}
          </Link>
        ))}
      </nav>
      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
        {user?.isAdmin && (
          <Link
            to="/admin"
            style={{ fontSize: 12, fontWeight: 700, padding: "3px 8px", background: "var(--ink)", color: "var(--bg)", borderRadius: 4, textDecoration: "none" }}
          >
            ADMIN
          </Link>
        )}
        {user ? (
          <>
            <button
              onClick={() => navigate("/account")}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
              title={`${user.first} ${user.last}`}
            >
              <UserAvatar first={user.first} last={user.last} size={32} />
            </button>
            <button
              onClick={() => {
                signOut();
                navigate("/");
              }}
              style={{ background: "none", border: "1px solid var(--line)", padding: "6px 12px", borderRadius: 999, fontSize: 13, fontWeight: 600 }}
            >
              Sign out
            </button>
          </>
        ) : (
          <button
            onClick={() => navigate("/signin")}
            style={{ background: "var(--accent)", color: "var(--accent-ink)", border: "none", padding: "8px 16px", borderRadius: 999, fontSize: 13, fontWeight: 600 }}
          >
            Sign in
          </button>
        )}
      </div>
    </header>
  );
}

// Every destination in NAV_ITEMS. Account / Sign in deliberately isn't here —
// the header carries it at every width, so a tab would just duplicate it and
// cost a slot.
function MobileNav() {
  const loc = useLocation();
  const items = NAV_ITEMS;
  return (
    // Same here: .mobile-only owns `display`, so it stays out of the inline style.
    <nav
      style={{
        position: "sticky",
        bottom: 0,
        background: "rgba(255,255,255,0.96)",
        backdropFilter: "blur(14px)",
        borderTop: "1px solid var(--line)",
        justifyContent: "space-around",
        padding: "10px 8px 14px",
        zIndex: 5,
      }}
      className="mobile-only"
    >
      {items.map((n) => {
        const active = loc.pathname === n.path;
        return (
          <Link
            key={n.path}
            to={n.path}
            aria-current={active ? "page" : undefined}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
              textDecoration: "none",
              color: active ? "var(--accent)" : "var(--ink-soft)",
              fontSize: 11,
              fontWeight: 600,
              whiteSpace: "nowrap",
              // Floor the hit area at ~44px per accessibility guidance; the
              // labels alone leave some tabs only 32px wide. Six tabs at this
              // size still fit a 320px viewport without wrapping.
              minWidth: 44,
              padding: "4px 0",
            }}
          >
            <n.icon active={active} />
            {n.short}
          </Link>
        );
      })}
    </nav>
  );
}

function Footer() {
  return (
    <footer
      style={{
        borderTop: "1px solid var(--line)",
        padding: "28px 24px",
        textAlign: "center",
        fontSize: 13,
        color: "var(--ink-soft)",
        background: "var(--surface)",
      }}
    >
      <div>
        Built with love for kids fighting eye cancer. All funds raised go to{" "}
        <a
          href="https://wechope.org/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "var(--ink)", fontWeight: 700 }}
        >
          World Eye Cancer Hope
        </a>
        .
      </div>
      <div style={{ fontSize: 12, maxWidth: 640, margin: "10px auto 0" }}>
        Ride4Baker is Scott's personal fundraiser. This site only tracks miles and pledges —
        it never collects payments or card details. Donations are made by you, directly on
        GoFundMe. We are an independent fundraiser and are not operated by World Eye Cancer
        Hope (wechope.org).
      </div>
    </footer>
  );
}

function HomeIcon({ active }: { active: boolean }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M3 11l9-8 9 8v10a2 2 0 01-2 2h-4v-7H9v7H5a2 2 0 01-2-2V11z" stroke={active ? "var(--accent)" : "currentColor"} strokeWidth="1.6" />
    </svg>
  );
}

function PledgeIcon({ active }: { active: boolean }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M12 7v10m0 0l-4-4m4 4l4-4M4 20h16" stroke={active ? "var(--accent)" : "currentColor"} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

// A pledge you've already made — the arrow becomes a tick.
function MyPledgeIcon({ active }: { active: boolean }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M8 12l3 3 5-6M4 20h16" stroke={active ? "var(--accent)" : "currentColor"} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function AboutIcon({ active }: { active: boolean }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke={active ? "var(--accent)" : "currentColor"} strokeWidth="1.6" />
      <path d="M12 11v5M12 7.6v.6" stroke={active ? "var(--accent)" : "currentColor"} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function GuestbookIcon({ active }: { active: boolean }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M4 5.5A1.5 1.5 0 015.5 4H19v16H5.5A1.5 1.5 0 014 18.5v-13z" stroke={active ? "var(--accent)" : "currentColor"} strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M8 9h7M8 13h5" stroke={active ? "var(--accent)" : "currentColor"} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function PhotosIcon({ active }: { active: boolean }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="5" width="18" height="14" rx="2" stroke={active ? "var(--accent)" : "currentColor"} strokeWidth="1.6" />
      <circle cx="8.5" cy="10" r="1.5" stroke={active ? "var(--accent)" : "currentColor"} strokeWidth="1.4" />
      <path d="M4 17l5-4.5 4 3.5 3-2.5 4 3.5" stroke={active ? "var(--accent)" : "currentColor"} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
